import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Header from '@amzn/awsui-components-console/header';
import BreadcrumbGroup from '@amzn/awsui-components-console/breadcrumb-group';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Badge from '@amzn/awsui-components-console/badge';
import Grid from '@amzn/awsui-components-console/grid';
import Container from '@amzn/awsui-components-console/container';
import Button from '@amzn/awsui-components-console/button';
import Spinner from '@amzn/awsui-components-console/spinner';
import Table from '@amzn/awsui-components-console/table';
import Modal from '@amzn/awsui-components-console/modal';
import { useDescribeStringQuery } from 'src/generated/graphql';
import { validateStringDetailParams } from './validation';
import SourceTextPanel from './components/SourceTextPanel';
import MetaBox from './components/MetaBox';
import TranslationProgressPanel from './components/TranslationProgressPanel';
import VariantsTable from './components/VariantsTable';
import LocalesTable from './components/LocalesTable';

/** TEMPORARY: Hardcoded demo values for design mockup parity.
 *  These fields have NO backend API support. Remove when real API fields exist. */
const DEMO_ONLY_FIELDS = {
  createdOn: 1772323200, // 2026-03-01 00:00:00 UTC → 01/03/2026
  createdBy: 'menshara@',
  owner: 'FelineUserAcceptanceTesters',
  permissions: 'Bindle_Link',
  psmsId: 'PSMS-ID-696cbab1-f860',
  anchor: 'TS:v0:c2e34a94-d444',
} as const;

/** Allowed actions per version history entry — mockup logic:
 *  - Oldest/first version (v1): only "view" (nothing before it to diff or rollback from)
 *  - Newest "unchanged" versions: only "diff" (can diff against prior, but no view/rollback since unchanged)
 *  - Normal intermediate versions: diff + view + rollback */
type VersionAction = 'diff' | 'view' | 'rollback';

interface VersionHistoryEntry {
  version: string;
  summary: string;
  date: string;
  author: string;
  actions: VersionAction[];
}

/** DEMO-ONLY: Hardcoded version history entries for mockup visual parity.
 *  No backend API for individual string version history exists today.
 *  `ListStringGroupVersions` covers string GROUPS, not per-string diffs.
 *  Remove this constant when a real string version history API is added. */
const DEMO_VERSION_HISTORY: VersionHistoryEntry[] = [
  {
    version: 'v10',
    summary: 'Updated source text for clarity',
    date: '02/07/2026',
    author: 'menshara@',
    actions: ['diff', 'view', 'rollback'],
  },
  {
    version: 'v5',
    summary: 'Added context note for translators',
    date: '15/05/2026',
    author: 'jdoe@',
    actions: ['diff', 'view', 'rollback'],
  },
  {
    version: 'v2',
    summary: 'Initial translation submitted',
    date: '10/03/2026',
    author: 'menshara@',
    actions: ['diff', 'view', 'rollback'],
  },
  {
    version: 'v1',
    summary: 'String created',
    date: '01/03/2026',
    author: 'menshara@',
    actions: ['view'],
  },
];

const StringDetailPage: React.FC = () => {
  const { namespaceId, stringGroupId, stringId } = useParams<{
    namespaceId: string;
    stringGroupId: string;
    stringId: string;
  }>();
  const [searchParams] = useSearchParams();
  const variant = searchParams.get('variant');
  const versionParam = searchParams.get('version');

  // Version is a String type in GraphQL (package convention for Long values)
  const parsedVersion = versionParam ?? undefined;

  // Version History toggle (base view only, demo-only)
  const [versionHistoryVisible, setVersionHistoryVisible] = useState(false);

  // DEMO-ONLY: Version history action modals
  const [viewModalEntry, setViewModalEntry] =
    useState<VersionHistoryEntry | null>(null);
  const [diffModalEntry, setDiffModalEntry] =
    useState<VersionHistoryEntry | null>(null);
  const [rollbackModalEntry, setRollbackModalEntry] =
    useState<VersionHistoryEntry | null>(null);
  const [rollbackSuccess, setRollbackSuccess] = useState(false);

  // Client-side validation
  const validationErrors = validateStringDetailParams({
    namespaceId,
    stringGroupId,
    stringId,
    variant,
    version: versionParam,
  });

  const hasValidationErrors = validationErrors.length > 0;

  // Only fire query if validation passes
  const { data, loading, error } = useDescribeStringQuery({
    variables: {
      namespaceId: namespaceId || '',
      stringGroupId: stringGroupId || '',
      stringId: stringId || '',
      cxId: variant || undefined,
      stringGroupVersion: parsedVersion,
    },
    skip: hasValidationErrors,
  });

  // When in variant view, fetch the base string separately to display the base
  // source text for comparison. The variant-filtered response only returns the
  // resolved (variant) text — the base text requires a second call without cxId.
  // Design decision: we pass the same stringGroupVersion to the base-string
  // comparison call so both primary and base views reflect the same historical
  // snapshot. This ensures consistent data when viewing a pinned version.
  const isVariantView = !!variant;
  const { data: baseData, loading: baseLoading } = useDescribeStringQuery({
    variables: {
      namespaceId: namespaceId || '',
      stringGroupId: stringGroupId || '',
      stringId: stringId || '',
      cxId: undefined,
      stringGroupVersion: parsedVersion,
    },
    skip: hasValidationErrors || !isVariantView,
  });

  // Construct paths for navigation — simplified flat URL (Item 4)
  const basePath = `/${encodeURIComponent(namespaceId || '')}/${encodeURIComponent(stringGroupId || '')}/${encodeURIComponent(stringId || '')}`;

  // Breadcrumb items — namespace points to /:namespaceId route;
  // string-group has no route today so it renders as non-clickable text (empty href).
  const breadcrumbItems = [
    { text: 'Home', href: '/' },
    {
      text: namespaceId || '',
      href: `/${encodeURIComponent(namespaceId || '')}`,
    },
    {
      text: stringGroupId || '',
      href: '',
    },
  ];

  if (variant) {
    breadcrumbItems.push({ text: stringId || '', href: basePath });
    breadcrumbItems.push({ text: 'Variant', href: '' });
  } else {
    breadcrumbItems.push({ text: stringId || '', href: '' });
  }

  // Inner page content rendering — chrome (TopNav/LeftNav) is provided by AppShell
  const renderContent = () => {
    // Validation error state
    if (hasValidationErrors) {
      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <BreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Alert type="error" header="Validation Error">
            <ul>
              {validationErrors.map((err) => (
                <li key={err.field}>{err.message}</li>
              ))}
            </ul>
          </Alert>
        </SpaceBetween>
      );
    }

    // Loading state
    if (loading) {
      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <BreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Box textAlign="center" padding="xxl">
            <Spinner size="large" />
            <Box variant="p" margin={{ top: 's' }}>
              Loading string details…
            </Box>
          </Box>
        </SpaceBetween>
      );
    }

    // Error state
    if (error) {
      const isNotFound =
        error.message.includes('ResourceNotFoundException') ||
        error.message.includes('404') ||
        error.message.includes('not found');

      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <BreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Alert
            type="error"
            header={isNotFound ? 'String not found' : 'Error'}
          >
            {isNotFound
              ? `The string "${stringId}" was not found in namespace "${namespaceId}", string group "${stringGroupId}".`
              : `An error occurred while loading the string: ${error.message}`}
          </Alert>
        </SpaceBetween>
      );
    }

    const stringDetail = data?.describeString;
    if (!stringDetail) {
      return (
        <SpaceBetween size="l">
          <div data-testid="breadcrumb">
            <BreadcrumbGroup items={breadcrumbItems} />
          </div>
          <Alert type="warning" header="No data">
            No string data returned.
          </Alert>
        </SpaceBetween>
      );
    }

    const isVariant = !!variant;

    return (
      <SpaceBetween size="l">
        <div data-testid="breadcrumb">
          <BreadcrumbGroup items={breadcrumbItems} />
        </div>

        {/* Variant banner */}
        {isVariant && (
          <Container data-testid="variant-banner">
            <Box>
              <SpaceBetween
                size="xs"
                direction="horizontal"
                alignItems="center"
              >
                <Box variant="p" fontWeight="bold" color="text-status-info">
                  This is a Variant of {stringDetail.stringId}
                </Box>
                <Button
                  variant="link"
                  href={basePath}
                  data-testid="variant-banner-back-link"
                >
                  ← Back to Base String
                </Button>
              </SpaceBetween>
              <Box variant="small" color="text-status-inactive">
                CX ID: {variant} · This variant overrides the base string for
                this CX ID.
              </Box>
            </Box>
          </Container>
        )}

        {/* Page header with Version History button (base view only) */}
        <div data-testid="string-detail-title">
          <Header
            variant="h1"
            actions={
              !isVariant ? (
                <Button
                  variant="normal"
                  data-testid="version-history-toggle"
                  onClick={() =>
                    setVersionHistoryVisible(!versionHistoryVisible)
                  }
                >
                  Version History
                </Button>
              ) : undefined
            }
          >
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              {stringDetail.stringId}
              {isVariant ? (
                <Badge color="blue" data-testid="variant-badge">
                  Variant: {variant}
                </Badge>
              ) : (
                <Badge color="blue" data-testid="base-string-badge">
                  Base String
                </Badge>
              )}
              {parsedVersion != null && !hasValidationErrors && (
                <Badge color="grey" data-testid="version-pin-badge">
                  Version: v{parsedVersion}
                </Badge>
              )}
            </SpaceBetween>
          </Header>
        </div>

        {/* DEMO-ONLY: Version History panel (base view only).
            No backend API for individual string version history exists.
            Hardcoded data for mockup visual parity — remove when real API is added. */}
        {!isVariant && versionHistoryVisible && (
          <Container
            data-testid="version-history-panel"
            header={
              <Header
                variant="h2"
                actions={
                  <Button
                    variant="icon"
                    iconName="close"
                    ariaLabel="Close version history"
                    data-testid="version-history-close"
                    onClick={() => setVersionHistoryVisible(false)}
                  />
                }
              >
                Version History
              </Header>
            }
          >
            <SpaceBetween size="s">
              <Box
                variant="small"
                color="text-status-inactive"
                data-testid="version-history-note"
              >
                Showing last {DEMO_VERSION_HISTORY.length} versions
              </Box>
              <Table
                items={[...DEMO_VERSION_HISTORY]}
                columnDefinitions={[
                  {
                    id: 'version',
                    header: 'Version',
                    cell: (item) => (
                      <span
                        data-testid="version-item"
                        data-version={item.version}
                      >
                        <Box fontWeight="bold">{item.version}</Box>
                      </span>
                    ),
                    width: 80,
                  },
                  {
                    id: 'summary',
                    header: 'Change Summary',
                    cell: (item) => item.summary,
                  },
                  {
                    id: 'date',
                    header: 'Date',
                    cell: (item) => item.date,
                    width: 120,
                  },
                  {
                    id: 'author',
                    header: 'Author',
                    cell: (item) => item.author,
                    width: 120,
                  },
                  {
                    id: 'actions',
                    header: 'Actions',
                    cell: (item) => (
                      <SpaceBetween direction="horizontal" size="xs">
                        {item.actions.includes('diff') && (
                          <Button
                            variant="normal"
                            data-testid="version-action-diff"
                            onClick={() => setDiffModalEntry(item)}
                          >
                            Diff
                          </Button>
                        )}
                        {item.actions.includes('view') && (
                          <Button
                            variant="normal"
                            data-testid="version-action-view"
                            onClick={() => setViewModalEntry(item)}
                          >
                            View
                          </Button>
                        )}
                        {item.actions.includes('rollback') && (
                          <Button
                            variant="normal"
                            data-testid="version-action-rollback"
                            onClick={() => setRollbackModalEntry(item)}
                          >
                            Rollback
                          </Button>
                        )}
                      </SpaceBetween>
                    ),
                    width: 250,
                  },
                ]}
              />
            </SpaceBetween>
          </Container>
        )}

        {/* Two-column layout: source text (~75%) + metadata (~25%) using Grid */}
        <Grid
          gridDefinition={[
            { colspan: { default: 12, s: 9 } },
            { colspan: { default: 12, s: 3 } },
          ]}
        >
          <SourceTextPanel
            sourceLocale={stringDetail.sourceLocale}
            sourceText={stringDetail.sourceText}
            contextNote={stringDetail.contextNote}
            variantCxId={isVariant ? variant || undefined : undefined}
            baseSourceText={baseData?.describeString?.sourceText}
            baseSourceTextLoading={isVariant && baseLoading}
            stringId={stringDetail.stringId}
          />
          <MetaBox
            cxFamilyId={stringDetail.cxFamilyId}
            cxIds={stringDetail.cxIds}
            updatedAt={stringDetail.updatedAt}
            updatedBy={stringDetail.updatedBy}
            variantCxId={isVariant ? variant || undefined : undefined}
            baseStringHref={isVariant ? basePath : undefined}
            stringGroupVersion={
              isVariant ? stringDetail.stringGroupVersion : undefined
            }
            // Demo-only fields for design mockup parity (see DEMO_ONLY_FIELDS)
            createdOn={DEMO_ONLY_FIELDS.createdOn}
            createdBy={DEMO_ONLY_FIELDS.createdBy}
            stringGroupId={stringGroupId}
            owner={DEMO_ONLY_FIELDS.owner}
            permissions={DEMO_ONLY_FIELDS.permissions}
            psmsId={DEMO_ONLY_FIELDS.psmsId}
            anchor={DEMO_ONLY_FIELDS.anchor}
          />
        </Grid>

        {/* Translation progress */}
        <TranslationProgressPanel
          translationProgress={stringDetail.translationProgress}
          variantCxId={isVariant ? variant || undefined : undefined}
        />

        {/* Manage variants (only shown in base view) */}
        {!isVariant && (
          <VariantsTable
            variants={stringDetail.variants || []}
            baseUrl={basePath}
            versionParam={parsedVersion}
          />
        )}

        {/* Locales table — Item 1: bottom spacing via Box margin */}
        <Box margin={{ bottom: 'l' }}>
          <LocalesTable
            translations={data?.describeString?.translations || []}
            loading={loading}
            error={undefined}
          />
        </Box>
      </SpaceBetween>
    );
  };

  return (
    <>
      <main id="main-content">
        <div style={{ maxWidth: 1860, margin: '0 auto', padding: 24 }}>
          {renderContent()}
        </div>
      </main>

      {/* DEMO-ONLY: View version modal.
          No backend API exists for fetching individual version snapshots.
          This shows static demo data only — nothing persists. */}
      {viewModalEntry && (
        <Modal
          visible={true}
          onDismiss={() => setViewModalEntry(null)}
          header={`View version ${viewModalEntry.version}`}
          footer={
            <Box float="right">
              <Button variant="primary" onClick={() => setViewModalEntry(null)}>
                Close
              </Button>
            </Box>
          }
        >
          <div data-testid="string-view-modal">
            <SpaceBetween size="m">
              <Box>
                <Box variant="strong">Version:</Box> {viewModalEntry.version}
              </Box>
              <Box>
                <Box variant="strong">Change:</Box> {viewModalEntry.summary}
              </Box>
              <Box>
                <Box variant="strong">Date:</Box> {viewModalEntry.date}
              </Box>
              <Box>
                <Box variant="strong">Author:</Box> {viewModalEntry.author}
              </Box>
              <Alert type="info">
                DEMO ONLY: This is a static snapshot. No real version data is
                fetched.
              </Alert>
            </SpaceBetween>
          </div>
        </Modal>
      )}

      {/* DEMO-ONLY: Diff version modal.
          No backend API exists for computing per-string diffs.
          This shows static placeholder content only. */}
      {diffModalEntry && (
        <Modal
          visible={true}
          onDismiss={() => setDiffModalEntry(null)}
          header={`Diff — ${diffModalEntry.version} vs previous`}
          footer={
            <Box float="right">
              <Button variant="primary" onClick={() => setDiffModalEntry(null)}>
                Close
              </Button>
            </Box>
          }
        >
          <div data-testid="string-diff-modal">
            <SpaceBetween size="m">
              <Box>
                <Box variant="strong">Version:</Box> {diffModalEntry.version}
              </Box>
              <Box>
                <Box variant="strong">Summary:</Box> {diffModalEntry.summary}
              </Box>
              <Box variant="code">
                {`- Previous source text\n+ ${diffModalEntry.summary}`}
              </Box>
              <Alert type="info">
                DEMO ONLY: This is a static placeholder diff. No real diff
                computation exists.
              </Alert>
            </SpaceBetween>
          </div>
        </Modal>
      )}

      {/* DEMO-ONLY: Rollback confirmation modal.
          No backend API exists for rolling back individual string versions.
          Nothing persists — this is purely UI demonstration. */}
      {rollbackModalEntry && !rollbackSuccess && (
        <Modal
          visible={true}
          onDismiss={() => setRollbackModalEntry(null)}
          header={`Rollback to ${rollbackModalEntry.version}`}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => setRollbackModalEntry(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setRollbackSuccess(true)}
                >
                  Confirm rollback
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <div data-testid="rollback-modal">
            <SpaceBetween size="m">
              <Box>
                Are you sure you want to rollback to version{' '}
                <Box variant="strong" display="inline">
                  {rollbackModalEntry.version}
                </Box>
                ?
              </Box>
              <Box>
                <Box variant="strong">Change at that version:</Box>{' '}
                {rollbackModalEntry.summary}
              </Box>
              <Alert type="warning">
                DEMO ONLY: No real rollback will occur. This is a UI
                demonstration only.
              </Alert>
            </SpaceBetween>
          </div>
        </Modal>
      )}

      {/* DEMO-ONLY: Rollback success confirmation */}
      {rollbackSuccess && (
        <Modal
          visible={true}
          onDismiss={() => {
            setRollbackSuccess(false);
            setRollbackModalEntry(null);
          }}
          header="Rollback successful"
          footer={
            <Box float="right">
              <Button
                variant="primary"
                onClick={() => {
                  setRollbackSuccess(false);
                  setRollbackModalEntry(null);
                }}
              >
                Done
              </Button>
            </Box>
          }
        >
          <div data-testid="rollback-success-modal">
            <Alert type="success">
              Successfully rolled back to version {rollbackModalEntry?.version}.
              (DEMO ONLY — no data was actually changed.)
            </Alert>
          </div>
        </Modal>
      )}
    </>
  );
};

export default StringDetailPage;
